import React from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import type { Glp1Article } from '../types'
import useLocale from 'js/hooks/useLocale'
import { trackGlp1CtaClick } from 'js/analytics'

interface PostInListProps {
  article: Glp1Article
}

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`

const Card = styled.article`
  border-radius: var(--border-radius-md);
  overflow: hidden;
  transition:
    transform var(--transition-base),
    box-shadow var(--transition-base);
  cursor: pointer;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`

const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  aspect-ratio: 3/2;
  width: 353px;
  height: 353px;

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
  }
`

const Picture = styled.picture`
  display: block;
  width: 100%;
  height: 100%;
`

const Image = styled.img`
  margin: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--transition-slow);
`

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(0, 0, 0, 0.7) 100%
  );
`

const TextOverlay = styled.div`
  position: absolute;
  bottom: var(--spacing-md);
  left: var(--spacing-md);
  right: 25%;
`

const Title = styled.h3`
  margin: 0;
  font-size: var(--font-size-lg);
  font-weight: 600;
  line-height: var(--line-height-heading);
  color: #fff;
  text-wrap: balance;
`

const PostInList: React.FC<PostInListProps> = ({ article }) => {
  const { language } = useLocale()

  return (
    <StyledLink
      to={`/${language}/health/${article.slug}`}
      onClick={() =>
        trackGlp1CtaClick({ source: 'article', articleSlug: article.slug })
      }
    >
      <Card>
        <ImageContainer>
          {article.featuredImage && (
            <Picture>
              {article.featuredImage.responsiveImage.webpSrcSet && (
                <source
                  srcSet={article.featuredImage.responsiveImage.webpSrcSet}
                  sizes="(min-width: 768px) 353px, 100vw"
                  type="image/webp"
                />
              )}
              <Image
                src={article.featuredImage.responsiveImage.src}
                srcSet={article.featuredImage.responsiveImage.srcSet}
                sizes="(min-width: 768px) 353px, 100vw"
                alt={article.featuredImage.responsiveImage.alt || article.title}
                loading="lazy"
              />
            </Picture>
          )}
          <Overlay />
          <TextOverlay>
            <Title>{article.title} →</Title>
          </TextOverlay>
        </ImageContainer>
      </Card>
    </StyledLink>
  )
}

export default PostInList
